import type { Dispatch, UnknownAction } from '@reduxjs/toolkit'

import { defineListener, errorView, type ModuleListener } from '@krp/client-runtime'

import { classifyCommandError } from '../../../shared/model/commandOutcome'
import {
  catalogRefreshRequested,
  productBarcodeRequested,
  productMarkDeletedRequested,
  productOperationResendRequested,
  productReloadRequested,
  productSaveRequested,
} from './commands'
import { cardFields } from './fields'
import { draftKey, normalizeRid, operationKey } from './keys'
import { operationCancelled, operationPrepared, operationResent, operationSettled, operationStarted } from './operationsSlice'
import type { ProductsPorts, ProductsPortsFactory } from './ports'
import { draftDiscarded } from './productsSlice'
import {
  CATALOG_REFRESH_KIND,
  CATALOG_TARGET,
  NEW_PRODUCT,
  type ProductAction,
  type ProductDraft,
  type ProductOperation,
  type ProductOperationPayload,
  type ProductOperationsState,
  type ProductsIntentState,
} from './types'

export interface ProductsStateAccess<S> {
  intent: (state: S) => ProductsIntentState
  operations: (state: S) => ProductOperationsState
}

/**
 * Что listener видит во время сценария. `owns` — операция всё ещё ЭТА: её не
 * сменила вторая команда (двойной клик), не стёр выход пользователя или
 * очистка базы. Проверяется после каждого `await` и перед отправкой: иначе
 * воскресшая команда ушла бы в 1С от имени следующего человека.
 */
interface Scenario {
  ports: ProductsPorts
  dispatch: Dispatch<UnknownAction>
  operations: () => ProductOperationsState
}

const isBusy = (operation: ProductOperation | undefined): boolean =>
  operation !== undefined && (operation.status === 'sending' || operation.status === 'unknown')

const owns = (scenario: Scenario, operation: ProductOperation): boolean => {
  const current = scenario.operations()[operation.key]
  return current !== undefined && current.operationId === operation.operationId && current.status === 'sending'
}

function settle(scenario: Scenario, operation: ProductOperation, patch: Omit<Parameters<typeof operationSettled>[0], 'key' | 'operationId'>) {
  scenario.dispatch(operationSettled({ key: operation.key, operationId: operation.operationId, ...patch }))
}

/**
 * Отправить операцию и записать итог. «Неизвестно» здесь не повторяется
 * никогда: решает человек (ADR-11, D-085).
 */
async function sendAndSettle(scenario: Scenario, operation: ProductOperation) {
  if (!owns(scenario, operation)) return
  const { ports, dispatch } = scenario
  const outcome = await ports.send(operation)
  if (outcome.ok) {
    settle(scenario, operation, { status: 'succeeded', resendable: false, error: null, result: outcome.value })
    if (operation.action === 'update' || operation.action === 'create') {
      dispatch(draftDiscarded({ resourceId: operation.resourceId, target: operation.target }))
    }
    ports.reloadLists(operation.resourceId)
    return
  }
  const failure = classifyCommandError(outcome.error)
  settle(scenario, operation, { status: failure.status, resendable: failure.resendable, error: failure.error, result: null })
  // Конфликт или неизвестный исход: экран должен показать то, что в 1С сейчас.
  if (operation.target !== NEW_PRODUCT && operation.target !== CATALOG_TARGET && (failure.status === 'conflict' || failure.status === 'unknown')) {
    ports.reloadProduct(operation.resourceId, operation.target)
  }
}

/**
 * Начать операцию: она встаёт «в пути» СРАЗУ, до любого ожидания, — второе
 * нажатие видит занятость и новой команды не создаёт. `undefined` — занято.
 */
function begin(
  scenario: Scenario,
  base: { resourceId: string; target: string; action: ProductAction; detail?: string },
  expectedConfigurationVersion: string,
  payload: ProductOperationPayload,
): ProductOperation | undefined {
  const key = operationKey(base.resourceId, base.target, base.action, base.detail)
  if (isBusy(scenario.operations()[key])) return undefined
  const operationId = scenario.ports.newOperationId()
  const operation: ProductOperation = {
    key,
    resourceId: normalizeRid(base.resourceId),
    target: base.target,
    action: base.action,
    operationId,
    // Ключ привязан к операции: одинаковый ключ в двух базах — две операции и на сервере.
    idempotencyKey: `onec-ui:${base.action}:${operationId}`,
    expectedConfigurationVersion,
    payload,
    status: 'sending',
    resendable: false,
    error: null,
    result: null,
    startedAt: scenario.ports.now(),
  }
  scenario.dispatch(operationStarted(operation))
  return owns(scenario, operation) ? operation : undefined
}

/** Отказ ЧТЕНИЯ до команды: в 1С ничего не отправлялось — «не выполнено», а не «неизвестно». */
function refuseBeforeSend(scenario: Scenario, operation: ProductOperation, raw: unknown) {
  const failure = classifyCommandError(raw)
  settle(scenario, operation, {
    status: failure.status === 'unknown' ? 'failed' : failure.status,
    resendable: false,
    error: failure.error,
    result: null,
  })
}

async function saveUpdate(scenario: Scenario, draft: ProductDraft) {
  const operation = begin(scenario, { resourceId: draft.resourceId, target: draft.target, action: 'update' }, draft.baseVersion ?? '', {
    kind: 'update',
  })
  if (operation === undefined) return
  // Свежая карточка мимо кэша: правка по устаревшему виду затёрла бы чужое изменение.
  const fresh = await scenario.ports.readCard(draft.resourceId, draft.target)
  if (!owns(scenario, operation)) return
  if (!fresh.ok) {
    refuseBeforeSend(scenario, operation, fresh.error)
    return
  }
  const server = cardFields(fresh.value.data)
  const changed: { name?: string; article?: string } = {}
  let conflict = false
  for (const field of ['name', 'article'] as const) {
    const mine = draft.values[field]
    if (mine === undefined || mine === server[field]) continue
    if (server[field] !== draft.base[field]) conflict = true
    changed[field] = mine
  }
  if (conflict) {
    settle(scenario, operation, {
      status: 'conflict',
      resendable: false,
      error: errorView({ message: 'Карточку изменили в 1С после начала правки: сравните значения и решите, что оставить.' }),
      result: null,
    })
    scenario.ports.reloadProduct(draft.resourceId, draft.target)
    return
  }
  if (Object.keys(changed).length === 0) {
    // Требуемое уже в 1С: команды нет, черновик закрыт.
    scenario.dispatch(operationCancelled({ key: operation.key, operationId: operation.operationId }))
    scenario.dispatch(draftDiscarded({ resourceId: draft.resourceId, target: draft.target }))
    return
  }
  const prepared: ProductOperation = {
    ...operation,
    expectedConfigurationVersion: draft.baseVersion ?? fresh.value.configurationVersion,
    payload: { kind: 'update', ...changed },
  }
  scenario.dispatch(operationPrepared(prepared))
  await sendAndSettle(scenario, prepared)
}

async function saveCreate(scenario: Scenario, draft: ProductDraft) {
  const name = (draft.values.name ?? '').trim()
  if (name === '') return
  const article = (draft.values.article ?? '').trim()
  const barcode = (draft.values.barcode ?? '').trim()
  const operation = begin(scenario, { resourceId: draft.resourceId, target: NEW_PRODUCT, action: 'create' }, '', {
    kind: 'create',
    name,
    article: article === '' ? null : article,
    barcode: barcode === '' ? null : barcode,
  })
  if (operation === undefined) return
  const version = await scenario.ports.readVersion(draft.resourceId, name)
  if (!owns(scenario, operation)) return
  if (!version.ok) {
    refuseBeforeSend(scenario, operation, version.error)
    return
  }
  const prepared = { ...operation, expectedConfigurationVersion: version.value }
  scenario.dispatch(operationPrepared(prepared))
  await sendAndSettle(scenario, prepared)
}

/**
 * «Обновить каталог из 1С» (B-3 приёмки U9): ревизия и умение базы — свежим чтением, затем `Catalog_TriggerSync`.
 * Если база обновление товаров не умеет — отказ словами до команды. «Ещё выполняется» служба отдаёт как 409 с
 * повтором тем же ключом: это «Проверить исход» окна, а не новая команда.
 */
async function refreshCatalog(scenario: Scenario, resourceId: string) {
  const operation = begin(scenario, { resourceId, target: CATALOG_TARGET, action: 'catalog-refresh' }, '', {
    kind: 'catalog-refresh',
    refreshKind: CATALOG_REFRESH_KIND,
  })
  if (operation === undefined) return
  const sync = await scenario.ports.readSyncVersion(resourceId)
  if (!owns(scenario, operation)) return
  if (!sync.ok) {
    refuseBeforeSend(scenario, operation, sync.error)
    return
  }
  if (!sync.value.refreshKinds.includes(CATALOG_REFRESH_KIND)) {
    settle(scenario, operation, {
      status: 'failed',
      resendable: false,
      error: errorView({ message: 'Служба 1С не умеет обновлять товары этой базы — обновление не запускалось.' }),
      result: null,
    })
    return
  }
  const prepared = { ...operation, expectedConfigurationVersion: sync.value.configurationVersion }
  scenario.dispatch(operationPrepared(prepared))
  await sendAndSettle(scenario, prepared)
}

/** Listeners фичи: команда → порт. Состояние модуля читают только через `access`. */
export function createProductsListeners<S>(
  makePorts: ProductsPortsFactory,
  access: ProductsStateAccess<S>,
): ModuleListener<S>[] {
  const scenarioOf = (api: { dispatch: Dispatch<UnknownAction>; getModuleState: () => S }): Scenario => ({
    ports: makePorts(api.dispatch),
    dispatch: api.dispatch,
    operations: () => access.operations(api.getModuleState()),
  })
  return [
    defineListener<S, ReturnType<typeof productSaveRequested>>(productSaveRequested.match, async (action, api) => {
      const draft = access.intent(api.getModuleState()).drafts[draftKey(action.payload.resourceId, action.payload.target)]
      if (draft === undefined) return
      const scenario = scenarioOf(api)
      if (draft.target === NEW_PRODUCT) await saveCreate(scenario, draft)
      else await saveUpdate(scenario, draft)
    }),

    defineListener<S, ReturnType<typeof productOperationResendRequested>>(
      productOperationResendRequested.match,
      async (action, api) => {
        const operation = access.operations(api.getModuleState())[action.payload.key]
        // Повтор — только явным действием человека и только той же командой.
        if (operation === undefined || operation.status === 'sending' || !operation.resendable) return
        api.dispatch(operationResent({ key: operation.key }))
        await sendAndSettle(scenarioOf(api), { ...operation, status: 'sending' })
      },
    ),

    defineListener<S, ReturnType<typeof catalogRefreshRequested>>(catalogRefreshRequested.match, async (action, api) => {
      await refreshCatalog(scenarioOf(api), action.payload.resourceId)
    }),

    defineListener<S, ReturnType<typeof productMarkDeletedRequested>>(productMarkDeletedRequested.match, async (action, api) => {
      const { resourceId, refKey, expectedConfigurationVersion } = action.payload
      const scenario = scenarioOf(api)
      const operation = begin(scenario, { resourceId, target: refKey, action: 'delete' }, expectedConfigurationVersion, {
        kind: 'delete',
      })
      if (operation !== undefined) await sendAndSettle(scenario, operation)
    }),

    defineListener<S, ReturnType<typeof productBarcodeRequested>>(productBarcodeRequested.match, async (action, api) => {
      const { resourceId, refKey, barcode, expectedConfigurationVersion } = action.payload
      const code = barcode.trim()
      if (code === '') return
      const scenario = scenarioOf(api)
      const operation = begin(
        scenario,
        { resourceId, target: refKey, action: action.payload.action, detail: code },
        expectedConfigurationVersion,
        { kind: 'barcode', barcode: code },
      )
      if (operation !== undefined) await sendAndSettle(scenario, operation)
    }),

    defineListener<S, ReturnType<typeof productReloadRequested>>(productReloadRequested.match, (action, api) => {
      const ports = makePorts(api.dispatch)
      if (action.payload.refKey === null) ports.reloadLists(action.payload.resourceId)
      else ports.reloadProduct(action.payload.resourceId, action.payload.refKey)
    }),
  ]
}
